/**
 * UserRow Utility Functions
 * Helper functions for formatting and processing user data in admin tables.
 */

// Helper function to format numbers with commas
export const formatNumber = (num) => {
  if (!num && num !== 0) return '0';
  return num.toLocaleString();
};

// Helper function to format time remaining with granular details
export const formatTimeRemaining = (timeDiffMs) => {
  const days = Math.floor(timeDiffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((timeDiffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((timeDiffMs % (1000 * 60 * 60)) / (1000 * 60));
  
  const parts = [];
  
  if (days > 0) {
    parts.push(`${days} day${days !== 1 ? 's' : ''}`);
  }
  
  if (hours > 0) {
    parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
  }
  
  // Only show minutes if we don't have days (to avoid too much detail)
  if (minutes > 0 && days === 0) {
    parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
  }
  
  // If no parts, show minutes anyway
  if (parts.length === 0) {
    parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
  }
  
  // Join with "and" for the last item, commas for others
  if (parts.length === 1) {
    return `${parts[0]} left`;
  } else if (parts.length === 2) {
    return `${parts[0]} and ${parts[1]} left`;
  } else {
    const lastPart = parts.pop();
    return `${parts.join(', ')}, and ${lastPart} left`;
  }
};

// Helper function to get day of the week in CST timezone
export const getDayOfWeekInCST = (date) => {
  if (!date) return null;
  
  // Convert to CST timezone
  const cstDate = new Date(date.toLocaleString("en-US", {timeZone: "America/Chicago"}));
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return dayNames[cstDate.getDay()];
};

// Helper function to format date and time in CST
export const formatDateTimeInCST = (date) => {
  if (!date) return null;
  
  return date.toLocaleString("en-US", {
    timeZone: "America/Chicago",
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Helper function to get the best available name
export const getUserDisplayName = (user) => {
  // Try displayName first
  if (user.displayName && user.displayName.trim() && user.displayName !== 'No Name') {
    return user.displayName.trim();
  }
  
  // Try firstName + lastName
  if (user.firstName && user.lastName) {
    return `${user.firstName.trim()} ${user.lastName.trim()}`;
  }
  
  // Try just firstName
  if (user.firstName && user.firstName.trim()) {
    return user.firstName.trim();
  }
  
  // Try just lastName
  if (user.lastName && user.lastName.trim()) {
    return user.lastName.trim();
  }
  
  // Extract from email as last resort
  if (user.email) {
    const emailPart = user.email.split('@')[0];
    const cleanName = emailPart.replace(/[^a-zA-Z]/g, '');
    if (cleanName.length > 0) {
      return cleanName.charAt(0).toUpperCase() + cleanName.slice(1);
    }
  }
  
  return 'No Name';
};

// Helper function to calculate next decision date (payment or cancellation)
export const getNextDecisionDate = (user, formatDate) => {
  let decisionDate = null;
  let decisionType = '';
  
  const now = new Date();
  
  // Debug logging for Chris Shepley
  if (user.email && user.email.includes('shepdog925')) {
    console.log('🚀 getNextDecisionDate called for Chris:', {
      subscriptionCancelAt: user.subscriptionCancelAt,
      subscriptionPeriodEnd: user.subscriptionPeriodEnd,
      trialEnd: user.trialEnd,
      planInterval: user.planInterval
    });
  }
  
  // For non-renewing users, calculate when their subscription actually ends
  // Priority 1: If user has scheduled cancellation, use subscriptionPeriodEnd (when it actually ends)
  if (user.subscriptionCancelAt && user.subscriptionPeriodEnd) {
    // Use subscriptionPeriodEnd as that's when the subscription actually ends
    decisionDate = user.subscriptionPeriodEnd?.toDate ? user.subscriptionPeriodEnd.toDate() : new Date(user.subscriptionPeriodEnd);
    decisionType = 'Subscription ends';
  }
  // Priority 2: If user has active subscription, next billing is the decision date
  else if (user.subscriptionPeriodEnd) {
    decisionDate = user.subscriptionPeriodEnd?.toDate ? user.subscriptionPeriodEnd.toDate() : new Date(user.subscriptionPeriodEnd);
    decisionType = 'Next billing';
  }
  // Priority 3: If user is in trial, trial end is the decision date
  else if (user.trialEnd) {
    decisionDate = user.trialEnd?.toDate ? user.trialEnd.toDate() : new Date(user.trialEnd);
    decisionType = 'Trial ends';
  }
  
  // If we have a date but it's in the past, calculate the next billing cycle
  if (decisionDate && decisionDate < now) {
    const interval = user.planInterval || user.subscriptionInterval || 'month';
    const isAnnual = interval.toLowerCase().includes('year') || interval.toLowerCase().includes('annual');
    
    // Calculate next billing date from the past date
    const monthsToAdd = isAnnual ? 12 : 1;
    const nextDate = new Date(decisionDate);
    
    // Keep adding billing cycles until we get a future date
    while (nextDate < now) {
      nextDate.setMonth(nextDate.getMonth() + monthsToAdd);
    }
    
    decisionDate = nextDate;
    decisionType = user.subscriptionCancelAt ? 'Subscription ends' : 'Next billing';
  }
  
  if (!decisionDate || isNaN(decisionDate.getTime())) {
    return null;
  }
  
  const timeDiff = decisionDate.getTime() - now.getTime();
  const daysDiff = timeDiff / (1000 * 60 * 60 * 24);
  
  // If still in the past (shouldn't happen now), mark as overdue
  if (timeDiff <= 0) {
    const daysOverdue = Math.abs(Math.floor(daysDiff));
    return { 
      text: `${daysOverdue} day${daysOverdue !== 1 ? 's' : ''} overdue`, 
      date: formatDate(decisionDate), 
      type: decisionType,
      isOverdue: true,
      totalMinutes: 0
    };
  }
  
  // Calculate future countdown using the new granular formatter
  const countdownText = formatTimeRemaining(timeDiff);
  
  return { 
    text: countdownText, 
    date: formatDate(decisionDate), 
    type: decisionType,
    isOverdue: false,
    totalMinutes: Math.floor(timeDiff / (1000 * 60)) // For sorting
  };
};

// Helper function to format phone number for display
export const formatPhoneDisplay = (phone) => {
  // Handle phone data - could be object, string, or missing
  let raw = null;
  if (phone) {
    if (typeof phone === 'object') {
      raw = phone.e164 || phone.national || null;
    } else if (typeof phone === 'string') {
      raw = phone;
    }
  }
  
  // If no valid phone data, return null
  if (!raw || typeof raw !== 'string' || raw.trim() === '') {
    return null;
  }

  // Normalize to string and strip all non-digits
  const value = String(raw).trim();
  const digits = value.replace(/\D/g, '');
  
  // If no digits found, return null
  if (!digits || digits.length === 0) {
    return null;
  }

  let display = value;
  let telHref = value.startsWith('+') ? value : `+${digits}`;

  // Format common US pattern: 1 + 10 digits or 10 digits
  if (digits.length === 11 && digits.startsWith('1')) {
    const area = digits.slice(1, 4);
    const prefix = digits.slice(4, 7);
    const line = digits.slice(7, 11);
    display = `(${area}) ${prefix}-${line}`;
  } else if (digits.length === 10) {
    const area = digits.slice(0, 3);
    const prefix = digits.slice(3, 6);
    const line = digits.slice(6, 10);
    display = `(${area}) ${prefix}-${line}`;
    telHref = `+1${digits}`;
  } else if (value.startsWith('+') && digits.length > 7) {
    // Generic international grouping +XX XXXX XXXX (best-effort)
    const country = digits.slice(0, 2);
    const rest = digits.slice(2);
    display = `+${country} ${rest.replace(/(\d{3,4})(?=\d)/g, '$1 ').trim()}`;
  }

  return { display, telHref };
};

// Helper to get subscription badge info
export const getSubscriptionBadgeInfo = (user) => {
  // Try multiple sources for subscription interval data
  const interval = user.planInterval || user.subscriptionInterval || user.lastKnownInterval;
  
  // For users without clear interval data, determine what to show
  if (!interval || interval === 'N/A' || interval === 'null' || interval === null || interval === '') {
    // If they have revenue data, they were paying customers
    if (user.totalRevenue > 0 || user.firstPaymentDate) {
      // Try to infer from monthly price or default to Monthly for former paying customers
      if (user.monthlyPrice >= 20) {
        return { text: 'Annual', className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' };
      } else if (user.monthlyPrice > 0) {
        return { text: 'Monthly', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' };
      } else {
        return { text: 'Former Paid', className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' };
      }
    }
    
    // For trial users (no revenue), show what type of trial they had
    if (user.stripeCustomerId || user.hasHadFreeTrial) {
      if (user.monthlyPrice && user.monthlyPrice >= 20) {
        return { text: 'Annual Trial', className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' };
      } else if (user.monthlyPrice && user.monthlyPrice > 0) {
        return { text: 'Monthly Trial', className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' };
      } else {
        return { text: 'Monthly Trial', className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' };
      }
    }
    
    // Not a former paying customer or trial user
    return { text: 'Basic', className: 'text-gray-400' };
  }
  
  const normalizedInterval = interval.toLowerCase();
  
  if (normalizedInterval.includes('year') || normalizedInterval.includes('annual')) {
    return { text: 'Annual', className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' };
  } else if (normalizedInterval.includes('month')) {
    return { text: 'Monthly', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' };
  } else {
    return { text: interval, className: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' };
  }
};

